import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material";
import { FALLBACK_CARE_CATEGORIES, FALLBACK_QUESTIONS, FALLBACK_TREATMENTS, INFO_PAGES, money } from "../data/site";
import { errorMessage, getIntakeQuestions, getMyVisits, getTreatment, getTreatmentCategories, submitVisit } from "../services/veysoApi";
import { useApiData } from "../hooks/useApiData";
import { useAuth0 } from "../hooks/useAuth0";
import FallbackNotice from "../components/FallbackNotice";
import { Card, Cards, Eyebrow, HeroPanel, PageHero, Section, heroButtonSx } from "../components/ui";
import { palette5, tokens } from "../theme";

export function GetCare() {
  return (
    <>
      <HeroPanel theme="navy" eyebrow="Get Care" title="Treatment is a relationship"
        subtitle="Consult. Prescribe. Monitor. Adjust. Repeat. Your provider stays involved throughout."
        actions={<>
          <Button size="large" component={RouterLink} to="/get-care/women" sx={heroButtonSx("navy")}>Women's care</Button>
          <Button size="large" component={RouterLink} to="/get-care/men" sx={heroButtonSx("navy", "ghost")}>Men's care</Button>
        </>}
        minHeight={480}
      />
      <Section>
        <Cards min={240}>
          {[["Physician reviewed", "A licensed provider reviews every request."], ["Safety screened", "Your history is checked before any plan."],
            ["Ongoing monitoring", "Labs and check-ins, not a one-time visit."], ["In-person if needed", "Some people are directed to in-person care."]].map(([t, d]) => (
            <Card key={t}><Typography variant="h4" sx={{ fontSize: 20 }}>{t}</Typography><Typography sx={{ color: tokens.muted, mt: 1 }}>{d}</Typography></Card>
          ))}
        </Cards>
      </Section>
    </>
  );
}

export function CareAudience() {
  const { audience } = useParams();
  const aud = audience === "men" ? "men" : "women";
  const { data, usingFallback } = useApiData(() => getTreatmentCategories(aud), FALLBACK_CARE_CATEGORIES.filter((c) => c.audience === aud), { deps: [aud] });
  return (
    <>
      <PageHero theme="navy" eyebrow="Get Care" title={aud === "men" ? "Men's care" : "Women's care"}
        subtitle="Pick an area of focus. You answer a short questionnaire and a licensed provider reviews it." />
      <Section>
        <FallbackNotice show={usingFallback} />
        <Eyebrow>Areas of focus</Eyebrow>
        <Cards min={280}>
          {(data || []).map((c) => (
            <Card key={c.slug} sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h4" sx={{ fontSize: 22 }}>{c.name}</Typography>
              <Typography sx={{ color: tokens.muted, mt: 1, flex: 1 }}>{c.blurb}</Typography>
              <Button component={RouterLink} to={`/treatment/${c.slug}-visit`} variant="outlined" sx={{ mt: 3, alignSelf: "start" }}>See options</Button>
            </Card>
          ))}
        </Cards>
      </Section>
    </>
  );
}

export function TreatmentDetail() {
  const { slug } = useParams();
  const fallback = FALLBACK_TREATMENTS.find((t) => t.slug === slug) || null;
  const { data: t, loading, usingFallback } = useApiData(() => getTreatment(slug), fallback, { deps: [slug] });
  if (loading) return null;
  if (!t) return <PageHero theme="navy" eyebrow="Get Care" title="Not found" actions={<Button component={RouterLink} to="/get-care" sx={heroButtonSx("navy")}>Back to care</Button>} />;
  return (
    <>
      <PageHero theme="navy" eyebrow="Online visit" title={t.name} subtitle={t.summary}
        actions={<Button size="large" component={RouterLink} to={`/visit/${t.slug}`} sx={heroButtonSx("navy")}>Start your visit</Button>} />
      <Section tone="white" narrow>
        <FallbackNotice show={usingFallback} />
        <Typography sx={{ fontSize: 17 }}>{t.detail}</Typography>
        <Typography sx={{ mt: 3, color: tokens.muted }}>
          Sample price from {money(t.monthlyCents)} per month. You are only charged if a provider decides a plan is appropriate (payments are not connected in this preview).
        </Typography>
        <Button sx={{ mt: 3 }} component={RouterLink} to="/get-care">Back to care</Button>
      </Section>
    </>
  );
}

export function InfoPage() {
  const { topic } = useParams();
  const p = INFO_PAGES[topic];
  if (!p) return <PageHero theme="navy" eyebrow="Get Care" title="Page not found" />;
  return (
    <>
      <PageHero theme={p.theme} eyebrow={p.eyebrow} title={p.title} subtitle="Sample page for the preview build." />
      <Section tone="white" narrow>
        {p.body.map((b) => <Typography key={b} sx={{ mb: 2, fontSize: 17 }}>{b}</Typography>)}
        <Button component={RouterLink} to="/get-care" variant="outlined">Get Care</Button>
      </Section>
    </>
  );
}

export function Visit() {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const fallback = FALLBACK_TREATMENTS.find((t) => t.slug === slug) || null;
  const { data: t } = useApiData(() => getTreatment(slug), fallback, { deps: [slug] });
  const { data: questions, usingFallback } = useApiData(getIntakeQuestions, FALLBACK_QUESTIONS);
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState({ busy: false, error: "", done: false });
  const set = (k) => (e) => setAnswers({ ...answers, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, error: "", done: false });
    try {
      await submitVisit({ treatmentSlug: slug, consent, answers });
      setState({ busy: false, error: "", done: true });
    } catch (err) {
      setState({ busy: false, error: errorMessage(err), done: false });
    }
  };

  return (
    <Section narrow>
      <Eyebrow>Online visit</Eyebrow>
      <Typography variant="h2" sx={{ fontSize: { xs: 30, md: 42 }, mb: 1 }}>{t ? t.name : "Online visit"}</Typography>
      <Typography sx={{ color: tokens.muted, mb: 3 }}>A licensed provider reviews your answers and decides what is appropriate. Sample questionnaire: VEYSO's clinical team will approve the real one.</Typography>
      <FallbackNotice show={usingFallback} />
      {!isAuthenticated ? (
        <Card>
          <Typography sx={{ mb: 2 }}>Create a free account or log in to start your visit. Your answers are private health information.</Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button variant="contained" component={RouterLink} to="/signup">Create account</Button>
            <Button variant="outlined" component={RouterLink} to="/login">Log in</Button>
          </Box>
        </Card>
      ) : state.done ? (
        <Alert severity="success" action={<Button color="inherit" onClick={() => navigate("/visits")}>My visits</Button>}>
          Your request was received. In this preview no provider is connected, so nothing happens next.
        </Alert>
      ) : (
        <Card>
          <Box component="form" onSubmit={submit} sx={{ display: "grid", gap: 2.25 }}>
            {state.error && <Alert severity="error">{state.error}</Alert>}
            {(questions || []).map((q) => q.type === "choice" ? (
              <TextField key={q.key} select label={q.label} value={answers[q.key] || ""} onChange={set(q.key)} required={q.required}>
                {q.options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            ) : (
              <TextField key={q.key} label={q.label} value={answers[q.key] || ""} onChange={set(q.key)} required={q.required} multiline minRows={2} inputProps={{ maxLength: 1000 }} />
            ))}
            <FormControlLabel
              control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
              label="I consent to receive care by telehealth and understand a provider decides what is appropriate. (Sample consent text.)"
            />
            <Button type="submit" variant="contained" size="large" disabled={state.busy || !consent} sx={{ justifySelf: "start" }}>{state.busy ? "Sending..." : "Submit for review"}</Button>
          </Box>
        </Card>
      )}
    </Section>
  );
}

export function MyVisits() {
  const { data, loading, error } = useApiData(getMyVisits);
  return (
    <Section>
      <Eyebrow>Consultations</Eyebrow>
      <Typography variant="h2" sx={{ fontSize: { xs: 30, md: 42 }, mb: 3 }}>My visits</Typography>
      {loading && <Typography>Loading...</Typography>}
      {error && <Alert severity="warning">Could not load your visits. {errorMessage(error)}</Alert>}
      {data && data.length === 0 && <Typography sx={{ color: tokens.muted }}>No visits yet. <RouterLink to="/get-care">Start one</RouterLink>.</Typography>}
      <Cards min={280}>
        {(data || []).map((v) => (
          <Card key={v.id}>
            <Typography variant="h4" sx={{ fontSize: 20 }}>{v.treatmentName}</Typography>
            <Typography sx={{ color: palette5.moss[0], fontSize: 14, mt: 0.5 }}>{v.status}</Typography>
            <Typography sx={{ color: tokens.muted, fontSize: 14, mt: 1 }}>{new Date(v.createdAt).toLocaleString()}</Typography>
          </Card>
        ))}
      </Cards>
    </Section>
  );
}
